import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { sanitizeHtml } from "@/lib/sanitize";
import { HeroBrandLogo } from "@/components/landing/HeroBrandLogo";

interface AnchorPageHeroProps {
  imageUrl?: string | null;
  headline?: string | null;
  description?: string | null;
  subLabel?: string | null;
  imageCredit?: string | null;
  imageCreditUrl?: string | null;
  /** Optional Tango lockup pinned to the top-right of the hero image. Off by default. */
  showLogo?: boolean;
  /** Reports the hero's real rendered height so the page panel can sit right below it. */
  onHeightChange?: (height: number) => void;
}

export function AnchorPageHero({ imageUrl, headline, description, subLabel, imageCredit, imageCreditUrl, showLogo, onHeightChange }: AnchorPageHeroProps) {
  const resolvedImageUrl = imageUrl || "/placeholder.svg";
  const rootRef = useRef<HTMLDivElement>(null);

  // The hero sizes to its content on narrow screens, so its height is only
  // known at runtime. Report it (and keep reporting it as the copy rewraps).
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    window.addEventListener("resize", report);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", report);
    };
  }, [onHeightChange]);

  return (
    <div ref={rootRef} className="fixed inset-x-0 top-0 -z-10 min-h-[60svh] w-full">
      {/* Image clipper — keeps the photo cropped to the hero area */}
      <div className="absolute inset-0 overflow-hidden">
        {/*
          Render the LCP hero image as a real <img> with fetchpriority="high"
          so the browser preload scanner discovers it immediately. CSS
          background-images are invisible to the preloader and hurt LCP.
        */}
        <img
          src={resolvedImageUrl}
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="high"
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {showLogo && <HeroBrandLogo />}

      {/* Photo credit overlay */}
      {imageCredit && (
        <div className="absolute bottom-2 right-3 z-20">
          {imageCreditUrl ? (
            <a
              href={imageCreditUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-white/5 hover:text-white/30 transition-colors"
            >
              📷 {imageCredit} / {imageCreditUrl?.includes("pexels") ? "Pexels" : "Unsplash"}
            </a>
          ) : (
            <span className="text-[10px] text-white/5">
              📷 {imageCredit} / {imageCreditUrl?.includes("pexels") ? "Pexels" : "Unsplash"}
            </span>
          )}
        </div>
      )}

      {/* Content — guarantees minimum top offset so it never sits under the header,
          and is allowed to grow beyond the image clipper on short viewports. */}
      <div className="relative z-10 flex min-h-[60svh] flex-col justify-end pt-28 md:pt-[160px] pb-10 md:pb-16">
        <div className="container-padding mx-auto w-full">
          {subLabel && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-accent font-semibold tracking-wide uppercase text-sm mb-3"
            >
              {subLabel}
            </motion.p>
          )}
          {headline && (
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl leading-tight"
            >
              {headline}
            </motion.h1>
          )}
          {description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="max-w-3xl text-lg md:text-xl leading-relaxed text-primary-foreground prose prose-invert [--tw-prose-body:hsl(var(--primary-foreground))] [--tw-prose-headings:hsl(var(--primary-foreground))] [--tw-prose-links:hsl(var(--primary-foreground))] [--tw-prose-bold:hsl(var(--primary-foreground))] [&_*]:!text-primary-foreground [&_p]:!my-0"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
